import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { fetchMagnumMoment } from '@/lib/magnumMoment'
import ScreenTimeCard from '@/components/ScreenTimeCard'

const categories = [
  {
    name: 'Reading',
    emoji: '📚',
    exercises: [
      {
        emoji: '🎤',
        name: 'Word Decode',
        description: 'Say the word out loud and Magnum will check if you got it right!',
        path: '/practice/decode',
      },
      {
        emoji: '🔤',
        name: 'Synonym Match',
        description: 'Pick the word that means the same thing. How many can you get?',
        path: '/practice/synonym-match',
      },
      {
        emoji: '🧩',
        name: 'Synonym Pairs',
        description: 'Find the matching word pairs hidden in the grid. Memory power!',
        path: '/practice/synonym-pairs',
      },
      {
        emoji: '✏️',
        name: 'Spelling',
        description: 'Hear a word, spell it out letter by letter!',
        path: '/practice/spelling',
      },
    ],
  },
  {
    name: 'Music',
    emoji: '🎵',
    exercises: [
      {
        emoji: '🎵',
        name: 'Note Naming',
        description: 'See a note on the staff and type its letter. Fast as you can!',
        path: '/practice/note-naming',
      },
      {
        emoji: '🎼',
        name: 'Interval Recognition',
        description: 'Name the interval you see on the staff!',
        path: '/practice/interval-recognition',
      },
      {
        emoji: '👂',
        name: 'Ear Training',
        description: 'Listen to the interval and identify it by ear!',
        path: '/practice/ear-training',
      },
      {
        emoji: '🎶',
        name: 'Melodic Dictation',
        description: 'Hear a melody and write it down note by note!',
        path: '/practice/melodic-dictation',
      },
      {
        emoji: '🎹',
        name: 'Harmonic Dictation',
        description: 'Listen to chord progressions and identify the Roman numerals!',
        path: '/practice/harmonic-dictation',
      },
    ],
  },
  {
    name: 'ACT',
    emoji: '🎯',
    exercises: [
      {
        emoji: '🎯',
        name: 'ACT Practice',
        description: 'One question at a time on the clock, or a whole section at test pace.',
        path: '/practice/act',
      },
      {
        emoji: '📈',
        name: 'My ACT Numbers',
        description: 'Your pace against the real clock, and what you actually get right.',
        path: '/practice/act/review',
      },
    ],
  },
]

export default function ExercisePickerPage() {
  const { user } = useAuth()
  const magnum = useMagnum()

  useEffect(() => {
    fetchMagnumMoment({ contextType: 'PICKING_GAME', streak: 0 })
      .then((m) => magnum.handleEngineResponse(m, 'happy'))
      .catch(() => {})
  }, [])

  if (!user || user.type !== 'child') return null

  return (
    <div className="space-y-8 py-4">
      <ScreenTimeCard />
      {categories.map((cat) => (
        <div key={cat.name} className="space-y-4">
          {categories.length > 1 && (
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <span>{cat.emoji}</span> {cat.name}
            </h2>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {cat.exercises.map((ex) => (
              <Link
                key={ex.path}
                to={ex.path}
                className="group block p-6 rounded-2xl border-2 border-border bg-background hover:border-primary hover:shadow-lg transition-all text-center space-y-3"
              >
                <div className="text-6xl group-hover:scale-110 transition-transform">{ex.emoji}</div>
                <h2 className="text-xl font-bold">{ex.name}</h2>
                <p className="text-sm text-muted-foreground">{ex.description}</p>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
